import { z } from "zod";

import {
  freshMealPlanTypeEnum,
  pricingPlanEnum,
  type TPricingPlan,
} from "../types/enums";
import type {
  TDiscount,
  TNibblesUpsellItem,
  TSprinklesUpsellItem,
  TCapiCustomData,
} from "./primitives";
import {
  nibblesUpsellItemSchema,
  sprinklesUpsellItemSchema,
  capiCustomDataSchema,
} from "./primitives";
import {
  getDefaultDiscountForTrialPlan,
  normaliseDiscount,
  normalisePlanType,
} from "../helpers/subscription-normalisation";
import {
  NIBBLES_TRIO_BUNDLE_PRICE_AFTER_DISCOUNT,
  NIBBLES_TRIO_BUNDLE_PRICE_BEFORE_DISCOUNT,
} from "../constants/defaults";

/**
 * Accepts legacy persisted shapes (e.g. Kotlin MealPlans enum names, string discounts)
 * and normalises to the current store contract.
 */
const loosePlanStructureSchema = z.object({
  planType: z.union([ pricingPlanEnum, z.string() ]).optional(),
  totalOriginalPrice: z.number().optional(),
  totalDiscountedPrice: z.number().optional(),
  originalPricePerDay: z.number().optional(),
  discountedPricePerDay: z.number().optional(),
  originalPricePerPortion: z.number().optional(),
  discountedPricePerPortion: z.number().optional(),
});

type NormalisedPlanStructure = {
  planType: TPricingPlan;
  totalOriginalPrice?: number;
  totalDiscountedPrice?: number;
  originalPricePerDay?: number;
  discountedPricePerDay?: number;
  originalPricePerPortion?: number;
  discountedPricePerPortion?: number;
};

function normalisePlan(
  plan: z.infer<typeof loosePlanStructureSchema> | undefined,
  fallback: TPricingPlan,
): NormalisedPlanStructure | undefined {
  if (!plan) { return undefined; }
  return {
    planType: normalisePlanType(plan.planType, fallback),
    totalOriginalPrice: plan.totalOriginalPrice,
    totalDiscountedPrice: plan.totalDiscountedPrice,
    originalPricePerDay: plan.originalPricePerDay,
    discountedPricePerDay: plan.discountedPricePerDay,
    originalPricePerPortion: plan.originalPricePerPortion,
    discountedPricePerPortion: plan.discountedPricePerPortion,
  };
}

/**
 * Migrates legacy tube nibble types (NIBBLES_CHICKEN / NIBBLES_SALMON) to NIBBLES_TRIO_BUNDLE.
 * Preserves the user's opt-in state so they see the trio bundle as already added on the Treats page.
 */
function normaliseNibblesUpsell(upsell: TNibblesUpsellItem | undefined): TNibblesUpsellItem | undefined {
  if (!upsell) { return undefined; }
  if (upsell.type === "NIBBLES_CHICKEN" || upsell.type === "NIBBLES_SALMON") {
    return {
      added: upsell.added,
      type: "NIBBLES_TRIO_BUNDLE",
      quantity: upsell.added ? 1 : 0,
      originalPrice: NIBBLES_TRIO_BUNDLE_PRICE_BEFORE_DISCOUNT,
      discountedPrice: NIBBLES_TRIO_BUNDLE_PRICE_AFTER_DISCOUNT,
    };
  }
  return upsell;
}

export const subscriptionDataSchema = z
  .object({
    discount: z.unknown().default({}),
    trial: loosePlanStructureSchema.optional(),
    ongoing: loosePlanStructureSchema.optional(),
    productType: freshMealPlanTypeEnum.optional(),
    nibblesUpsell: nibblesUpsellItemSchema.optional(),
    sprinklesUpsell: sprinklesUpsellItemSchema.optional(),
    capiCustomData: capiCustomDataSchema.optional(),
  })
  .transform((sub): {
    discount: TDiscount;
    trial?: NormalisedPlanStructure;
    ongoing?: NormalisedPlanStructure;
    productType?: z.infer<typeof freshMealPlanTypeEnum>;
    nibblesUpsell?: TNibblesUpsellItem;
    sprinklesUpsell?: TSprinklesUpsellItem;
    capiCustomData?: TCapiCustomData;
  } => {
    const trial = normalisePlan(sub.trial, "TRIAL");
    const ongoing = normalisePlan(sub.ongoing, "STANDARD");
    const trialPlan = trial?.planType ?? "TRIAL";
    const discount: TDiscount = normaliseDiscount(
      sub.discount,
      getDefaultDiscountForTrialPlan(trialPlan),
    ) ?? { discountCode: "" };
    const nibblesUpsell = normaliseNibblesUpsell(sub.nibblesUpsell);

    return {
      discount,
      trial,
      ongoing,
      productType: sub.productType,
      nibblesUpsell,
      sprinklesUpsell: sub.sprinklesUpsell,
      capiCustomData: sub.capiCustomData,
    };
  });

export type TSubscriptionData = z.infer<typeof subscriptionDataSchema>;
export type TSubscriptionDataInput = z.input<typeof subscriptionDataSchema>;
